import React from 'react';
import {
  Card,
  Row,
  Col,
  Tag,
  Button,
  Space,
  Badge,
  Tooltip,
  Divider,
  Typography,
} from 'antd';
import {
  CheckOutlined,
  EditOutlined,
  DeleteOutlined,
  StopOutlined,
  CheckCircleOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type { Plan } from '../../services/plan.service';

const { Title, Text } = Typography;

export interface PlanCardProps {
  plan: Plan;
  onEdit?: (plan: Plan) => void;
  onDelete?: (plan: Plan) => void;
  onToggleStatus?: (plan: Plan) => void;
  onViewTenants?: (plan: Plan) => void;
}

const PLAN_COLORS: Record<string, string> = {
  lite: '#722ed1',
  starter: '#fa8c16',
  professional: '#1890ff',
  enterprise: '#52c41a',
};

const PLAN_ICONS: Record<string, string> = {
  lite: '⭐',
  starter: '⭐⭐',
  professional: '⭐⭐⭐',
  enterprise: '⭐⭐⭐⭐',
};

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(value);

const formatLimit = (value: number | null): string =>
  value === null ? 'Không giới hạn' : value.toLocaleString('vi-VN');

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  onEdit,
  onDelete,
  onToggleStatus,
  onViewTenants,
}) => {
  const color = PLAN_COLORS[plan.slug] ?? '#8c8c8c';
  const icon = PLAN_ICONS[plan.slug] ?? '⭐';

  return (
    <Card
      style={{
        borderTop: `4px solid ${color}`,
        height: '100%',
        position: 'relative',
      }}
      hoverable
    >
      <div style={{ position: 'absolute', top: 16, right: 16 }}>
        <Badge
          status={plan.is_active ? 'success' : 'default'}
          text={plan.is_active ? 'Đang bật' : 'Đã tắt'}
        />
      </div>

      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Row align="middle" gutter={8}>
          <Col>
            <span style={{ fontSize: 24 }}>{icon}</span>
          </Col>
          <Col>
            <Title level={3} style={{ margin: 0, color }}>
              {plan.display_name}
            </Title>
          </Col>
        </Row>

        <Row align="middle" gutter={4}>
          <Col>
            <Text style={{ fontSize: 28, fontWeight: 'bold', color }}>
              {formatCurrency(plan.pricing.monthly)}
            </Text>
          </Col>
          <Col>
            <Text type="secondary">/tháng</Text>
          </Col>
        </Row>
        <Text type="secondary" style={{ fontSize: 13 }}>
          hoặc {formatCurrency(plan.pricing.yearly)} / năm
        </Text>

        <Tooltip title="Số tenant đang dùng gói này">
          <Tag color={color} icon={<TeamOutlined />} style={{ marginTop: 8 }}>
            {plan.tenant_count} tenant
          </Tag>
        </Tooltip>

        <Divider style={{ margin: '12px 0' }} />

        <div>
          <Text strong>Giới hạn:</Text>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
            <li>
              <Text>Cửa hàng: {formatLimit(plan.limits.max_stores)}</Text>
            </li>
            <li>
              <Text>Người dùng: {formatLimit(plan.limits.max_users)}</Text>
            </li>
            <li>
              <Text>Sản phẩm: {formatLimit(plan.limits.max_products)}</Text>
            </li>
          </ul>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <div>
          <Text strong>Tính năng:</Text>
          <div
            style={{ margin: '8px 0', display: 'flex', flexWrap: 'wrap', gap: 4 }}
          >
            {plan.features.slice(0, 6).map((feature) => (
              <Tag
                key={feature}
                icon={<CheckOutlined />}
                color={color}
                style={{ margin: 2 }}
              >
                {feature}
              </Tag>
            ))}
            {plan.features.length > 6 && (
              <Tooltip title={plan.features.slice(6).join(', ')}>
                <Tag>+{plan.features.length - 6}</Tag>
              </Tooltip>
            )}
          </div>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <Space style={{ width: '100%', justifyContent: 'flex-end' }} wrap>
          {onViewTenants && (
            <Button
              size="small"
              icon={<TeamOutlined />}
              onClick={() => onViewTenants(plan)}
            >
              Xem tenant
            </Button>
          )}
          {onToggleStatus && (
            <Button
              size="small"
              icon={plan.is_active ? <StopOutlined /> : <CheckCircleOutlined />}
              onClick={() => onToggleStatus(plan)}
            >
              {plan.is_active ? 'Vô hiệu hóa' : 'Kích hoạt'}
            </Button>
          )}
          {onEdit && (
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(plan)}
            >
              Sửa
            </Button>
          )}
          {onDelete && (
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => onDelete(plan)}
              disabled={plan.tenant_count > 0}
            >
              Xóa
            </Button>
          )}
        </Space>
      </Space>
    </Card>
  );
};

export default PlanCard;
